import { API, ApiPrefixes } from "../../api";
const BASE_URL = ApiPrefixes["memberManagement"];

export const memberExtendedApi = API.injectEndpoints({
  endpoints: (builder) => ({
    createMember: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/create?guard=staff`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: [{ type: "Member", id: "LIST" }],
    }),
    updateMember: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/${body.id}/update?guard=staff`,
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Member", id: arg?.id }],
    }),
    getMembers: builder.query({
      query: (page = 1) => ({
        url: `${BASE_URL}?guard=staff&page=${page}&limit=20`,
      }),
      providesTags: (result = {}) => {
        if (!result.members) return [{ type: "Member", id: "LIST" }];
        return [
          { type: "Member", id: "LIST" },
          ...result?.members?.map(({ id }) => ({ type: "Member", id: id })),
        ];
      },
      transformResponse: (responseData) => {
        const { members = [], pagination = {} } = responseData.data;
        return {
          members: members,
          pagination,
        };
      },
    }),
    viewMember: builder.query({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/view?guard=staff`,
      }),
      transformResponse: (responseData) => responseData.data,
      providesTags: (result, error, arg) => [{ type: "Member", id: arg }],
    }),
    deleteMember: builder.mutation({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/delete?guard=staff`,
        method: "DELETE",
      }),
      invalidatesTags: (result, err, arg) => [
        { type: "Member", id: arg },
        { type: "Member", id: "LIST" },
        { type: "Member", id: "ARCHIVED_LIST" },
        { type: "Member", id: "PENDING_LIST" },
      ],
    }),
    archiveMember: builder.mutation({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/archive?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: (result, err, arg) => [
        { type: "Member", id: arg },
        { type: "Member", id: "LIST" },
        { type: "Member", id: "ARCHIVED_LIST" },
      ],
    }),
    unarchiveMember: builder.mutation({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/unarchive?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: (result, err, arg) => [
        { type: "Member", id: arg },
        { type: "Member", id: "ARCHIVED_LIST" },
        { type: "Member", id: "LIST" },
      ],
    }),
    activateMember: builder.mutation({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/activate?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: (result, err, arg) => [
        { type: "Member", id: arg },
        { type: "Member", id: "ARCHIVED_LIST" },
        { type: "Member", id: "LIST" },
        { type: "Member", id: "PENDING_LIST" },
      ],
    }),
    unSuspendMember: builder.mutation({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/unsuspend?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Member", id: arg }],
    }),
    suspendMember: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/${body.memberId}/suspend?guard=staff`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: (result, err, arg) => [{ type: "Member", id: arg?.id }],
    }),
    getPendingMembers: builder.query({
      query: (page = 1) =>
        `${BASE_URL}/pending/pull/all?guard=staff&page=${page}`,
      providesTags: (result = {}) => {
        if (!result.members) return [{ type: "Member", id: "LIST" }];
        return [
          { type: "Member", id: "LIST" },
          ...result?.members?.map(({ id }) => ({ type: "Member", id: id })),
        ];
      },
      transformResponse: (responseData) => {
        const { members = [], pagination = {} } = responseData.data;
        return {
          members: members,
          pagination,
        };
      },
    }),
    getArchivedMembers: builder.query({
      query: (page = 1) =>
        `${BASE_URL}/archived?guard=staff&page=${page}&limit=20`,
      providesTags: (result = {}) => {
        if (!result.members) return [{ type: "Member", id: "ARCHIVED_LIST" }];
        return [
          { type: "Member", id: "ARCHIVED_LIST" },
          ...result?.members?.map(({ id }) => ({ type: "Member", id: id })),
        ];
      },
      transformResponse: (responseData) => {
        const { members = [], pagination = {} } = responseData.data || {};
        return {
          members: members,
          pagination,
        };
      },
    }),
    generateMemberId: builder.query({
      query: () => `${BASE_URL}/generate_code?guard=staff`,
      transformResponse: (responseData) => responseData.data,
    }),
    checkMemberNoAvailability: builder.query({
      query: (memberNo) =>
        `${BASE_URL}/check_member_no?guard=staff&member_no=${encodeURIComponent(
          memberNo || ""
        )}`,
      transformResponse: (responseData) => responseData?.data,
    }),
    searchMembers: builder.query({
      query: ({ page = 1, search = "" }) =>
        `${BASE_URL}/search/all?guard=staff&page=${page}&limit=20&search=${encodeURIComponent(search || "")}`,
      providesTags: (result = {}) => {
        if (!result.members) return [{ type: "Member", id: "LIST" }];
        return [
          { type: "Member", id: "LIST" },
          ...result?.members?.map(({ id }) => ({ type: "Member", id: id })),
        ];
      },
      transformResponse: (responseData) => {
        const { members = [], pagination = {} } = responseData.data || {};
        return {
          members: members,
          pagination,
        };
      },
    }),
    approveMember: builder.mutation({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/approve?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Member", id: "LIST" },
        { type: "Member", id: arg },
      ],
    }),
    bulkApproveMembers: builder.mutation({
      query: (memberIds) => ({
        url: `${BASE_URL}/bulk/approve?guard=staff`,
        method: "PUT",
        body: { member_ids: memberIds },
      }),
      invalidatesTags: (result, error, arg) => {
        const approvedIds = result?.data?.approved || result?.approved || [];
        const ids = Array.isArray(approvedIds) ? approvedIds : [];
        const argIds = Array.isArray(arg) ? arg : [];
        return [
          { type: "Member", id: "LIST" },
          ...[...new Set([...ids, ...argIds])].map((id) => ({
            type: "Member",
            id,
          })),
        ];
      },
    }),
    declineMember: builder.mutation({
      query: (memberId) => ({
        url: `${BASE_URL}/${memberId}/decline?guard=staff`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Member", id: "LIST" },
        { type: "Member", id: arg },
      ],
    }),
    changeMemberBranch: builder.mutation({
      query: ({ memberId, reqBody }) => ({
        url: `${BASE_URL}/${memberId}/change_branch?guard=staff`,
        method: "PATCH",
        body: reqBody,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Member", id: arg.memberId },
      ],
    }),
    downloadMemberCSV: builder.mutation({
      query: ({ search, page }) => ({
        url: `${BASE_URL}/download/csv?guard=staff&page=${page || 1}&limit=1000&search=${search || ""}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
        headers: {
          Accept: "*/*",
        },
      }),
    }),
    downloadMemberPDF: builder.mutation({
      query: ({ search, page }) => ({
        url: `${BASE_URL}/download/pdf?guard=staff&page=${page || 1}&limit=1000&search=${search || ""}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
        headers: {
          Accept: "*/*",
        },
      }),
    }),
    getOpeningBalanceCorrectionForm: builder.query({
      query: (memberId) => ({
        url: `${BASE_URL}/opening_balance_corrections/form?guard=staff&member_id=${memberId}`,
      }),
      transformResponse: (responseData) => responseData?.data,
      keepUnusedDataFor: 0,
    }),
    applyOpeningBalanceCorrection: builder.mutation({
      query: (body) => ({
        url: `${BASE_URL}/opening_balance_corrections/apply?guard=staff`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => {
        const mid = arg?.member_id;
        return [
          ...(mid ? [{ type: "Member", id: mid }] : []),
          { type: "member-contributions-history" },
          { type: "member-contributions-history-by-category-id" },
          { type: "member-contributions-history-v2", id: "list" },
          { type: "contribution-records" },
          { type: "member-transaction-history" },
          { type: "member-contributions-summary", id: "current" },
          { type: "OpeningBalanceCorrectionAudits", id: String(mid ?? "") },
          "member-opening-balance-corrections",
        ];
      },
    }),
    listStaffOpeningBalanceCorrections: builder.query({
      query: ({ memberId, page = 1, limit = 20 }) => ({
        url: `${BASE_URL}/opening_balance_corrections?guard=staff&member_id=${memberId}&page=${page}&limit=${limit}`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "OpeningBalanceCorrectionAudits", id: String(arg?.memberId ?? "") },
      ],
    }),
  }),
});

export const {
  useCreateMemberMutation,
  useGetMembersQuery,
  useViewMemberQuery,
  useDeleteMemberMutation,
  useArchiveMemberMutation,
  useUnSuspendMemberMutation,
  useSuspendMemberMutation,
  useUnarchiveMemberMutation,
  useActivateMemberMutation,
  useGetPendingMembersQuery,
  useGetArchivedMembersQuery,
  useGenerateMemberIdQuery,
  useLazyCheckMemberNoAvailabilityQuery,
  useSearchMembersQuery,
  useApproveMemberMutation,
  useBulkApproveMembersMutation,
  useDeclineMemberMutation,
  useUpdateMemberMutation,
  useChangeMemberBranchMutation,
  useDownloadMemberCSVMutation,
  useDownloadMemberPDFMutation,
  useGetOpeningBalanceCorrectionFormQuery,
  useApplyOpeningBalanceCorrectionMutation,
  useListStaffOpeningBalanceCorrectionsQuery,
} = memberExtendedApi;
